import React, { useState, useRef, useCallback } from "react";
import Icon from "./Icon";
import FileIcon from "./FileIcon";

function UploadModal({ onClose, onUpload, currentPath }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    addFiles(selected);
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    addFiles(dropped);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const addFiles = (newFiles) => {
    const fileEntries = newFiles.map((f) => ({
      file: f,
      name: f.name,
      size: formatSize(f.size),
      sizeBytes: f.size,
      ext: getExt(f.name),
      progress: 0,
      status: "pending",
    }));
    setFiles((prev) => [...prev, ...fileEntries]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);

    // Get raw File objects for real S3 upload
    const rawFileObjects = files.map((f) => f.file).filter(Boolean);

    // Update UI to show uploading state
    setFiles((prev) => prev.map((f) => ({ ...f, status: "uploading" })));

    // Format for display items
    const uploadedItems = files.map((f) => ({
      type: "file",
      name: f.name,
      ext: f.ext,
      size: f.size,
      modified: new Date().toISOString().split("T")[0],
      owner: "You",
    }));

    // Call parent with both formatted items AND raw File objects
    await onUpload(uploadedItems, rawFileObjects);
    setUploading(false);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="upload-modal-header">
          <div className="upload-modal-title">
            <Icon name="upload" style={{ color: "var(--accent)" }} />
            <h3>Upload Files</h3>
          </div>
          <div className="upload-path">
            <Icon name="folder" style={{ color: "#5f6368" }} />
            <span>{currentPath === "/" ? "All Files" : currentPath.split("/").pop()}</span>
          </div>
        </div>

        {/* Drop Zone */}
        {files.length === 0 && (
          <div
            className={`upload-dropzone ${dragOver ? "drag-over" : ""}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="dropzone-icon">
              <Icon name="cloudUpload" />
            </div>
            <h4>Drag & drop files here</h4>
            <p>or <span className="browse-link">browse from your computer</span></p>
            <p className="dropzone-hint">Supports all file types — PDF, DOCX, XLSX, images, ZIP, etc.</p>
          </div>
        )}

        {/* File List */}
        {files.length > 0 && (
          <div className="upload-file-list">
            <div
              className="upload-add-more"
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <Icon name="plus" />
              <span>Add more files</span>
            </div>

            {files.map((f, i) => (
              <div className="upload-file-item" key={i}>
                <div className="upload-file-icon">
                  <FileIcon type="file" ext={f.ext} />
                </div>
                <div className="upload-file-info">
                  <div className="upload-file-name">{f.name}</div>
                  <div className="upload-file-size">{f.size}</div>
                  {f.status === "uploading" && (
                    <div className="upload-progress-bar">
                      <div className="upload-progress-fill upload-progress-indeterminate"></div>
                    </div>
                  )}
                </div>
                <div className="upload-file-status">
                  {f.status === "pending" && (
                    <button className="upload-remove-btn" onClick={() => removeFile(i)}>
                      <Icon name="x" />
                    </button>
                  )}
                  {f.status === "uploading" && (
                    <Icon name="spinner" spin style={{ color: "var(--accent)" }} />
                  )}
                  {f.status === "done" && (
                    <Icon name="checkCircle" className="upload-done-icon" />
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {/* Actions */}
        <div className="upload-modal-actions">
          <button className="modal-btn cancel" onClick={onClose} disabled={uploading}>
            Cancel
          </button>
          <button
            className="modal-btn create"
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
          >
            {uploading ? (
              <><Icon name="spinner" spin /> Uploading...</>
            ) : (
              `Upload${files.length > 0 ? ` (${files.length})` : ""}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatSize(bytes) {
  if (bytes === 0) return "0 KB";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

function getExt(name) {
  const parts = name.split(".");
  if (parts.length < 2) return "generic";
  const ext = parts.pop().toLowerCase();
  const map = {
    pdf: "pdf", docx: "docx", doc: "docx",
    xlsx: "xlsx", xls: "xlsx", csv: "xlsx",
    pptx: "pptx", ppt: "pptx",
    png: "img", jpg: "img", jpeg: "img", gif: "img", svg: "img", webp: "img",
    zip: "zip", rar: "zip", "7z": "zip",
  };
  return map[ext] || "generic";
}

export default UploadModal;
